
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AddProductModal.css';
import { useApp, Product } from '../../context/AppContext';
import { useModalA11y } from '../../hooks/useModalA11y';
import { mediaAPI, productsAPI, aiAPI } from '../../services/api';
import { toast } from '../Toast/Toast';

interface AddProductModalProps {
  onClose: () => void;
  /** Optional — when present, the modal opens in edit mode and PATCHes
   *  the existing product instead of POSTing a new one. */
  editProduct?: Product;
}

const MAX_FILE_MB = 8;

const AddProductModal: React.FC<AddProductModalProps> = ({ onClose, editProduct }) => {
  const { addProduct, updateProduct } = useApp();
  const isEdit = !!editProduct;
  const [name, setName] = useState(editProduct?.name || '');
  const [description, setDescription] = useState(editProduct?.description || '');
  const [price, setPrice] = useState(editProduct ? String(editProduct.price) : '');
  const [category, setCategory] = useState(editProduct?.category || 'Hoodies');
  const [stock, setStock] = useState(editProduct ? String(editProduct.stock ?? 0) : '10');
  const [featured, setFeatured] = useState(!!editProduct?.featured);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>(editProduct?.images || []);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiClassifying, setAiClassifying] = useState(false);

  const handleAiDraftDescription = async () => {
    if (!name.trim()) {
      toast.error('Add a product name first — the AI needs something to write about.');
      return;
    }
    setAiDrafting(true);
    try {
      const res = await aiAPI.draftProductDescription({
        productName: name.trim(),
        category,
        price: price ? parseFloat(price) : undefined,
        notes: description.trim() || undefined,
      });
      if (res?.description) {
        setDescription(res.description);
      }
    } catch (e: any) {
      console.error('[ai/draft-product-description] failed:', e);
      toast.error(e?.message || 'AI draft failed');
    } finally {
      setAiDrafting(false);
    }
  };

  // Vision-based auto-fill. Sends the first uploaded image to the
  // llava model and back-fills any empty fields with the suggestion.
  // Never overwrites manual edits — only fills blanks. Lets the admin
  // upload a photo and one-tap-publish.
  const handleAiClassifyFromImage = async () => {
    const heroImage = uploadedImages[0];
    if (!heroImage) {
      toast.error('Upload a product image first.');
      return;
    }
    setAiClassifying(true);
    try {
      const res = await aiAPI.classifyProductImage(heroImage);
      const s = res?.suggestion || {};
      if (s.name && !name.trim()) setName(s.name);
      if (s.description && !description.trim()) setDescription(s.description);
      if (s.category && categories.includes(s.category)) setCategory(s.category);
      if (!s.name && !s.description && !s.category) {
        toast.error('AI could not parse the photo. Try again or fill manually.');
      }
    } catch (e: any) {
      console.error('[ai/classify-product-image] failed:', e);
      toast.error(e?.message || 'Vision classify failed');
    } finally {
      setAiClassifying(false);
    }
  };
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useModalA11y({ onEscape: onClose });

  const categories = ['Hoodies', 'Tops', 'Bottoms', 'Accessories'];

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const queue: File[] = [];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith('image/')) {
        toast.error(`${f.name} is not an image — skipped.`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_FILE_MB}MB — skipped.`);
        continue;
      }
      queue.push(f);
    }
    if (queue.length === 0) return;

    setUploading(true);
    try {
      const results = await Promise.all(queue.map(f => mediaAPI.uploadFile(f)));
      const urls = results.map(r => r.url).filter(Boolean) as string[];
      setUploadedImages(prev => [...prev, ...urls]);
    } catch (e: any) {
      console.error('product image upload failed', e);
      toast.error(e?.message || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url: string) => {
    setUploadedImages(prev => prev.filter(u => u !== url));
  };

  const handleSubmit = async () => {
    if (!name.trim() || !price) return;
    if (uploadedImages.length === 0) {
      toast.error('Add at least one product image.');
      return;
    }

    setSaving(true);
    try {
      const stockNum = Math.max(0, parseInt(stock || '0', 10) || 0);
      const sliceStock = Math.floor(stockNum / 4);

      if (isEdit && editProduct) {
        // Edit path — PATCH the existing row, then update local state.
        await productsAPI.update(editProduct.id, {
          name,
          description,
          price: parseFloat(price),
          category,
          imageUrl: uploadedImages[0],
          images: uploadedImages,
          featured,
          stock: stockNum,
        });
        updateProduct(editProduct.id, {
          name,
          description,
          price: parseFloat(price),
          category,
          images: uploadedImages,
          featured,
          stock: stockNum,
          sizes: [
            { size: 'S', stock: sliceStock },
            { size: 'M', stock: sliceStock },
            { size: 'L', stock: sliceStock },
            { size: 'XL', stock: sliceStock },
          ],
        });
      } else {
        await addProduct({
          name,
          description,
          price: parseFloat(price),
          images: uploadedImages,
          category,
          sizes: [
            { size: 'S', stock: sliceStock },
            { size: 'M', stock: sliceStock },
            { size: 'L', stock: sliceStock },
            { size: 'XL', stock: sliceStock }
          ],
          stock: stockNum,
          featured
        });
      }

      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      console.error('save product failed', e);
      toast.error(isEdit ? 'Failed to update product.' : 'Failed to create product.');
      setSaving(false);
    }
  };

  return createPortal((
    <div className="modal-overlay" onClick={onClose} ref={dialogRef} role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit product' : 'Add product'} tabIndex={-1}>
      <div className="add-product-modal animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        {success ? (
          <div className="product-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3>{isEdit ? 'Product Updated!' : 'Product Added!'}</h3>
            <p>{isEdit ? 'Your changes are live in the shop' : 'Your new product is now available in the shop'}</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="close-btn" onClick={onClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="product-form">
              <div className="form-group">
                <label>Product Images</label>
                <div className="product-image-uploader">
                  <div
                    className={`upload-drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                      e.preventDefault();
                      setDragOver(false);
                      handleFiles(e.dataTransfer.files);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  >
                    {uploading ? (
                      <>
                        <span className="spinner" />
                        <span>Uploading…</span>
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span>Drop images or click to upload</span>
                        <span className="upload-hint">JPG/PNG, up to {MAX_FILE_MB} MB each</span>
                      </>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={e => {
                      handleFiles(e.target.files);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  />

                  {uploadedImages.length > 0 && (
                    <div className="upload-thumbnails">
                      {uploadedImages.map((url, i) => (
                        <div key={url} className="upload-thumb">
                          <img src={url} alt="" loading="lazy" />
                          {i === 0 && <span className="primary-badge">Cover</span>}
                          <button
                            type="button"
                            className="thumb-remove"
                            onClick={() => removeImage(url)}
                            aria-label="Remove image"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadedImages.length > 0 && (
                    <button
                      type="button"
                      onClick={handleAiClassifyFromImage}
                      disabled={aiClassifying}
                      title="Vision-analyze the cover photo and fill empty fields automatically"
                      style={{
                        marginTop: 10,
                        background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 999,
                        padding: '8px 16px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: aiClassifying ? 'not-allowed' : 'pointer',
                        opacity: aiClassifying ? 0.6 : 1,
                      }}
                    >
                      {aiClassifying ? '…analyzing photo' : '✨ Auto-fill from photo'}
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  placeholder="e.g., Limited Edition Hoodie"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>Description</span>
                  <button
                    type="button"
                    onClick={handleAiDraftDescription}
                    disabled={aiDrafting || !name.trim()}
                    title={!name.trim() ? 'Add a product name first' : 'AI draft (overwrites existing text — current text is used as hint)'}
                    style={{
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: aiDrafting || !name.trim() ? 'not-allowed' : 'pointer',
                      opacity: aiDrafting || !name.trim() ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {aiDrafting ? '…drafting' : '✨ AI'}
                  </button>
                </label>
                <textarea
                  placeholder={aiDrafting ? 'AI is writing…' : 'Describe your product, or type a hint and tap ✨ AI'}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  disabled={aiDrafting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    placeholder="49.99"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Stock</label>
                  <input
                    type="number"
                    placeholder="10"
                    value={stock}
                    onChange={e => setStock(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)}>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={featured}
                  onChange={e => setFeatured(e.target.checked)}
                />
                <span>Feature this product</span>
              </label>
            </div>

            <button
              className={`save-product-btn ${saving ? 'saving' : ''}`}
              onClick={handleSubmit}
              disabled={!name.trim() || !price || saving || uploading || uploadedImages.length === 0}
            >
              {saving ? (
                <span className="spinner" />
              ) : (
                isEdit ? 'Save Changes' : 'Add Product'
              )}
            </button>
          </>
        )}
      </div>
    </div>
  ), document.body);
};

export default AddProductModal;
